import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import { authService } from '../services/authService';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [maskedEmail, setMaskedEmail] = useState('');
  const [error, setError] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('idle'); // idle, loading, success, error
  const [resendError, setResendError] = useState('');

  // Handle page loader fadeout
  useEffect(() => {
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$('.loader1').fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);

  // Verify email on mount
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Verification link is invalid. No token provided.');
      return;
    }

    const verifyEmail = async () => {
      try {
        const result = await authService.verifyEmail(token);
        setMaskedEmail(result.email);
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setError(err.message || 'Verification link is invalid or has expired.');
      }
    };

    verifyEmail();
  }, [token]);

  const handleResendVerification = async (e) => {
    e.preventDefault();

    if (!resendEmail) {
      setResendError('Please enter your email address');
      return;
    }

    setResendStatus('loading');
    setResendError('');

    try {
      await authService.resendVerification(resendEmail);
      setResendStatus('success');
    } catch (err) {
      if (err.status === 429) {
        const retryAfter = err.data?.retryAfter || 900;
        setResendError(`Too many requests. Please try again in ${Math.ceil(retryAfter / 60)} minutes.`);
      } else if (err.message?.includes('already verified')) {
        setResendError('This email is already verified. You can log in.');
      } else {
        // For security, show success even if email doesn't exist
        setResendStatus('success');
      }
      setResendStatus('error');
    }
  };


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[VerifyEmail] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={-100}
          className="paralax-section-slide-data1"
        />
      )}

      {/* Main Content */}
      <section className="login wide-tb-100">
        <div className="container">
          <div className="row">
            <div className="offset-lg-3 col-lg-6 col-md-12 col-sm-12 whitebox">
              <div className="widget logincontainer">

                {/* Verifying */}
                {status === 'verifying' && (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-3">Verifying your email...</p>
                  </div>
                )}

                {/* Success */}
                {status === 'success' && (
                  <div className="text-center">
                    <div className="alert alert-success" role="alert">
                      <h4 className="alert-heading">Email Verified!</h4>
                      <p>
                        Your email {maskedEmail && <strong>({maskedEmail})</strong>} has been verified successfully.
                      </p>
                      <hr />
                      <p className="mb-0">
                        You can now access all features of your account.
                      </p>
                    </div>
                    <Link to="/login" className="btn btn-default mt-3">
                      Go to Login
                    </Link>
                  </div>
                )}

                {/* Error */}
                {status === 'error' && (
                  <div className="text-center">
                    <div className="alert alert-danger" role="alert">
                      <h4 className="alert-heading">Verification Failed</h4>
                      <p>{error}</p>
                      <hr />
                      <p className="mb-0">
                        Verification links expire after 24 hours.
                      </p>
                    </div>

                    {/* Resend Verification Form */}
                    <div className="mt-4 text-left">
                      <h5 className="mb-3">Request a new verification email</h5>

                      {resendStatus === 'success' ? (
                        <div className="alert alert-success" role="alert">
                          If an account exists with that email and is not verified, a new verification link has been sent.
                        </div>
                      ) : (
                        <form onSubmit={handleResendVerification}>
                          {resendError && (
                            <div className="alert alert-danger" role="alert">
                              {resendError}
                            </div>
                          )}
                          <div className="form-group">
                            <input
                              type="email"
                              className="form-control"
                              placeholder="Enter your email"
                              value={resendEmail}
                              onChange={(e) => setResendEmail(e.target.value)}
                              required
                              disabled={resendStatus === 'loading'}
                            />
                          </div>
                          <button
                            type="submit"
                            className="btn btn-default"
                            disabled={resendStatus === 'loading'}
                          >
                            {resendStatus === 'loading' ? 'Sending...' : 'Resend Verification Email'}
                          </button>
                        </form>
                      )}
                    </div>

                    <div className="mt-4">
                      <Link to="/login" className="btn btn-link">
                        Back to Login
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default VerifyEmail;
